import { useState, type FormEvent } from "react";
import { toKey } from "./editor/shared";
import { useParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import type { Group } from "@workcore/schemas";
import { api, checkedWrite } from "../api";
import { KIND_LABEL, parsePrincipal, useDirectory, useGroups, useRefresh } from "../directory";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel } from "../ui";
import { HistoryLink } from "../activity";
import { askText, confirmAction } from "../confirm";

function GroupCard({ slug, g }: { slug: string; g: Group }) {
  const dir = useDirectory(slug);
  const refresh = useRefresh(slug);
  const [add, setAdd] = useState("");
  const base = `/v1/ws/${slug}/groups/${g.id}`;
  const addMember = useMutation({ mutationFn: (v: string) => checkedWrite("POST", `${base}/members`, { members: [parsePrincipal(v)] }), onSuccess: () => (setAdd(""), refresh()) });
  const removeMember = useMutation({ mutationFn: (v: string) => checkedWrite("DELETE", `${base}/members`, { members: [parsePrincipal(v)] }), onSuccess: refresh });
  const rename = useMutation({ mutationFn: (name: string) => checkedWrite("PATCH", base, { name }), onSuccess: refresh });
  const remove = useMutation({ mutationFn: () => api("DELETE", base), onSuccess: refresh });
  const memberKeys = new Set(g.members.map((m) => `${m.kind}:${m.id}`));
  const candidates = dir.options.filter((o) => !memberKeys.has(o.value) && o.value !== `group:${g.id}`);
  return (
    <Panel
      title={g.name}
      actions={
        <span className="actions">
          <span className="muted small">{g.members.length === 1 ? "1 member" : `${g.members.length} members`}</span>
          <code>{g.key}</code>
          <RowMenu
            label={`Actions for ${g.name}`}
            items={[
              { label: "Rename…", onSelect: () => void askText({ title: `Rename '${g.name}'`, action: "Rename", input: { label: "Name", value: g.name } }).then((n) => n && rename.mutate(n)) },
              { label: "Delete…", danger: true, onSelect: () => void confirmAction({ title: `Delete group '${g.name}'?`, body: "Its grants and roles stop applying to its members at once.", action: "Delete", check: () => api("DELETE", base, { dryRun: true }) }).then((ok) => ok && remove.mutate()) },
            ]}
          />
        </span>
      }
    >
      {g.description && <p className="muted">{g.description}</p>}
      <ul className="chips">
        {g.members.map((m) => (
          <li key={`${m.kind}:${m.id}`}>
            {dir.label(m)} <span className="muted small">{KIND_LABEL[m.kind]}</span>
            <button className="link" aria-label={`Remove ${dir.label(m)}`} onClick={() => void confirmAction({ title: `Remove ${dir.label(m)} from '${g.name}'?`, body: "The group's grants and roles stop applying to them. You can add them again.", action: "Remove", check: () => api("DELETE", `${base}/members`, { body: { members: [m] }, dryRun: true }) }).then((ok) => ok && removeMember.mutate(`${m.kind}:${m.id}`))}>×</button>
          </li>
        ))}
        {g.members.length === 0 && <li className="muted">No members</li>}
      </ul>
      <form autoComplete="off" className="inline" onSubmit={(e: FormEvent) => (e.preventDefault(), add && addMember.mutate(add))}>
        <label className="inline-field"><span>Add member</span><select value={add} onChange={(e) => setAdd(e.target.value)} aria-label={`Add member to ${g.name}`}>
          <option value="">Add a member…</option>
          {candidates.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select></label>
        <button type="submit" disabled={!add}>Add</button>
      </form>
      <ErrorBox error={addMember.error ?? removeMember.error ?? rename.error ?? remove.error} />
    </Panel>
  );
}

export function GroupsPage() {
  const { slug = "" } = useParams();
  const groups = useGroups(slug);
  const refresh = useRefresh(slug);
  const [key, setKey] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [adding, setAdding] = useState(false);
  const create = useMutation({
    mutationFn: () => checkedWrite("POST", `/v1/ws/${slug}/groups`, { key, name, description }),
    onSuccess: () => (setKey(""), setName(""), setDescription(""), setAdding(false), refresh()),
  });
  return (
    <>
      <PageHeader title="Groups" description="Groups hold users, service accounts and other groups, up to five levels deep. Give roles and access to a group instead of to each person." actions={<><HistoryLink slug={slug} kind="groups" /><button onClick={() => setAdding(true)}>New group</button></>} />
      <SidePanel title="New group" open={adding} onClose={() => setAdding(false)} onSubmit={() => create.mutate()} submitLabel="Create group" busy={create.isPending} error={create.error}>
        <Field label="Name">
          <input required value={name} autoFocus onChange={(e) => (setName(e.target.value), setKey(toKey(e.target.value)))} placeholder="Contractors" />
        </Field>
        <Field label="Key" help="Used in the API and CLI. It never changes.">
          <input required value={key} onChange={(e) => setKey(e.target.value.toLowerCase())} placeholder="contractors" />
        </Field>
        <Field label="Description" help="Optional.">
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </SidePanel>
      <ErrorBox error={groups.error} />
      {groups.data?.length === 0 && <EmptyState title="No groups yet" body="Put people who need the same access in a group, then give roles and project access to the group once." action={<button onClick={() => setAdding(true)}>New group</button>} />}
      {groups.data?.map((g) => <GroupCard key={g.id} slug={slug} g={g} />)}
    </>
  );
}
